import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { ArrowRight, Mail, Lock, User, Eye, EyeOff, ShieldCheck, ClipboardList, FlaskConical, ScanLine, CreditCard, CheckCircle, MapPin } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { useAuth, API } from "../../App";
import LanguageSwitcher from "../../components/LanguageSwitcher";

const btnGradient = "linear-gradient(180deg, #00aeef 0%, #00658d 100%)";
const clinicalShadow = "0 4px 20px rgba(0,101,141,0.06)";

export default function MobileLandingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { user, login } = useAuth();
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [registerForm, setRegisterForm] = useState({ name: "", email: "", password: "", age_confirmed: false });
  const [twoFactorState, setTwoFactorState] = useState({ active: false, token: "", email: "", code: "", resending: false, resendCooldown: 0 });

  // Hydrating 2FA + UI flag state from router location.state on mount — intentional pattern.
  React.useEffect(() => {
    if (location.state?.fromGoogle && location.state?.twoFactorToken) {
      setShowAuth(true);
      setTwoFactorState({ active: true, token: location.state.twoFactorToken, email: location.state.twoFactorEmail, code: "", resending: false, resendCooldown: 0 });
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  React.useEffect(() => {
    // Skip redirect if a 2FA challenge is in progress (just returned from
    // Google OAuth) — check both committed state and pending router state
    // since both effects fire on the same render.
    const pendingGoogle2FA = location.state?.fromGoogle && location.state?.twoFactorToken;
    if (user && !twoFactorState.active && !pendingGoogle2FA) navigate("/dashboard");
  }, [user, navigate, twoFactorState.active, location.state]);

  React.useEffect(() => {
    if (twoFactorState.resendCooldown <= 0) return;
    const timer = setTimeout(() => setTwoFactorState(prev => ({ ...prev, resendCooldown: prev.resendCooldown - 1 })), 1000);
    return () => clearTimeout(timer);
  }, [twoFactorState.resendCooldown]);

  const handleLogin = async (e) => {
    e.preventDefault(); setLoading(true); setError("");
    try {
      const response = await axios.post(`${API}/auth/login`, loginForm);
      if (response.data.requires_2fa) {
        setTwoFactorState({ active: true, token: response.data.two_factor_token, email: response.data.email, code: "", resending: false, resendCooldown: 0 });
        if (response.data.code_sent === false) setError(t('auth.codeSendFailed'));
        setLoading(false); return;
      }
      login(response.data.user, response.data.token);
      setTimeout(() => navigate("/dashboard"), 100);
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      setError(detail === 'wrong_password' ? t('auth.wrongPassword') : t('auth.loginFailed'));
    }
    setLoading(false);
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault(); setLoading(true); setError("");
    try {
      const response = await axios.post(`${API}/auth/2fa/verify`, { two_factor_token: twoFactorState.token, code: twoFactorState.code });
      login(response.data.user, response.data.token);
      setTimeout(() => navigate("/dashboard"), 100);
    } catch (err) { setError(err.response?.data?.detail || t('auth.verificationFailed')); }
    setLoading(false);
  };

  const handleResend2FA = async () => {
    setTwoFactorState(prev => ({ ...prev, resending: true })); setError("");
    try {
      await axios.post(`${API}/auth/2fa/resend`, { two_factor_token: twoFactorState.token });
      setTwoFactorState(prev => ({ ...prev, resending: false, resendCooldown: 60, code: "" }));
    } catch (err) { setError(err.response?.data?.detail || t('auth.resendFailed')); setTwoFactorState(prev => ({ ...prev, resending: false })); }
  };

  const handleBack2FA = () => { setTwoFactorState({ active: false, token: "", email: "", code: "", resending: false, resendCooldown: 0 }); setError(""); };

  const handleRegister = async (e) => {
    e.preventDefault(); setLoading(true); setError("");
    try {
      const response = await axios.post(`${API}/auth/register`, registerForm);
      login(response.data.user, response.data.token);
      navigate("/dashboard");
    } catch (err) { setError(err.response?.data?.detail || t('auth.registerFailed')); }
    setLoading(false);
  };

  const handleGoogleLogin = () => {
    // Clear any stale auth state so a previous user does not skip the 2FA
    // challenge issued for this fresh OAuth session.
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    localStorage.removeItem("justLoggedIn");
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Auth View
  if (showAuth) {
    return (
      <div className="min-h-screen" style={{ fontFamily: "'Inter', sans-serif", background: "#f7f9fe", color: "#181c20" }}>
        <header className="sticky top-0 z-50 backdrop-blur-xl" style={{ background: "rgba(248,250,254,0.8)", boxShadow: clinicalShadow }}>
          <div className="flex justify-between items-center px-6 py-4">
            <button onClick={() => setShowAuth(false)} className="text-sm font-medium" style={{ color: "#3e4850" }}>
              {"\u2190"} {t('common.back') || 'Tagasi'}
            </button>
            <LanguageSwitcher />
          </div>
        </header>

        <div className="max-w-lg mx-auto px-6 py-6">
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-4 overflow-hidden" style={{ background: "#ffffff", border: "1px solid #e2e8f0" }}>
              <img src="/brand/logo.webp" alt="Kilpnäärme Kliinik" className="w-14 h-14 object-contain" />
            </div>
            <h1 className="text-2xl font-bold" style={{ color: "#181c20" }}>{t('landing.title')}</h1>
          </div>

          {/* Auth Toggle */}
          <div className="flex rounded-xl p-1 mb-6" style={{ background: "#ebeef3" }}>
            <button onClick={() => setAuthMode("login")}
              className={`flex-1 py-3 rounded-lg text-sm font-semibold transition-all ${authMode === "login" ? "bg-white shadow-sm" : ""}`}
              style={{ color: authMode === "login" ? "#181c20" : "#6e7881" }}>
              {t('common.login')}
            </button>
            <button onClick={() => setAuthMode("register")}
              className={`flex-1 py-3 rounded-lg text-sm font-semibold transition-all ${authMode === "register" ? "bg-white shadow-sm" : ""}`}
              style={{ color: authMode === "register" ? "#181c20" : "#6e7881" }}>
              {t('common.register')}
            </button>
          </div>

          {error && <div className="mb-4 p-3 rounded-xl text-sm" style={{ background: "rgba(141,79,0,0.08)", color: "#6d3a00" }}>{error}</div>}

          {authMode === "login" && (
            <>
              {twoFactorState.active ? (
                <div className="space-y-5" data-testid="mobile-2fa-section">
                  <div className="text-center">
                    <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: "rgba(198,231,255,0.4)" }}>
                      <ShieldCheck className="w-7 h-7" style={{ color: "#00658D" }} />
                    </div>
                    <h2 className="text-lg font-bold mb-1">{t('auth.verifyIdentity') || 'Verify Your Identity'}</h2>
                    <p className="text-sm" style={{ color: "#6e7881" }}>{t('auth.codeSentTo') || 'A 6-digit code has been sent to'}</p>
                    <p className="text-sm font-semibold mt-1" style={{ color: "#3e4850" }}>{twoFactorState.email}</p>
                  </div>
                  <form onSubmit={handleVerify2FA} className="space-y-4">
                    <Input data-testid="mobile-2fa-code-input" type="text" inputMode="numeric" maxLength={6} placeholder="000000"
                      className="text-center text-2xl tracking-[0.5em] font-mono h-14 rounded-xl" value={twoFactorState.code}
                      onChange={(e) => { const val = e.target.value.replace(/\D/g, '').slice(0, 6); setTwoFactorState(prev => ({ ...prev, code: val })); }} autoFocus />
                    <button type="submit" data-testid="mobile-2fa-verify-btn" disabled={loading || twoFactorState.code.length !== 6}
                      className="w-full h-12 text-base rounded-full text-white font-semibold disabled:opacity-50" style={{ background: btnGradient }}>
                      {loading ? "..." : (t('auth.verify') || 'Verify')}
                    </button>
                  </form>
                  <div className="flex items-center justify-between">
                    <button type="button" onClick={handleBack2FA} className="text-sm" style={{ color: "#6e7881" }} data-testid="mobile-2fa-back-btn">
                      {"\u2190"} {t('auth.backToLogin') || 'Back'}
                    </button>
                    <button type="button" onClick={handleResend2FA} disabled={twoFactorState.resending || twoFactorState.resendCooldown > 0}
                      className="text-sm font-medium disabled:opacity-50" style={{ color: "#00658D" }} data-testid="mobile-2fa-resend-btn">
                      {twoFactorState.resendCooldown > 0 ? `${t('auth.resendIn') || 'Resend in'} ${twoFactorState.resendCooldown}s` : (t('auth.resendCode') || 'Resend code')}
                    </button>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label style={{ color: "#3e4850" }}>{t('common.email')}</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: "#bdc8d1" }} />
                      <Input type="email" value={loginForm.email} onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                        className="pl-11 h-12 text-base rounded-xl" placeholder={t('auth.emailPlaceholder')} required data-testid="mobile-login-email" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label style={{ color: "#3e4850" }}>{t('common.password')}</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: "#bdc8d1" }} />
                      <Input type={showPassword ? "text" : "password"} value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                        className="pl-11 pr-11 h-12 text-base rounded-xl" placeholder={t('auth.passwordPlaceholder')} required data-testid="mobile-login-password" />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: "#bdc8d1" }}>
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                  <button type="submit" disabled={loading} className="w-full h-12 text-base rounded-full text-white font-semibold disabled:opacity-50"
                    style={{ background: btnGradient }} data-testid="mobile-login-submit">
                    {loading ? "..." : t('auth.loginButton')}
                  </button>
                </form>
              )}
            </>
          )}

          {authMode === "register" && (
            <form onSubmit={handleRegister} className="space-y-4">
              <div className="space-y-2">
                <Label style={{ color: "#3e4850" }}>{t('common.name')}</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: "#bdc8d1" }} />
                  <Input type="text" value={registerForm.name} onChange={(e) => setRegisterForm({ ...registerForm, name: e.target.value })}
                    className="pl-11 h-12 text-base rounded-xl" placeholder={t('auth.namePlaceholder')} required data-testid="mobile-register-name" />
                </div>
              </div>
              <div className="space-y-2">
                <Label style={{ color: "#3e4850" }}>{t('common.email')}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: "#bdc8d1" }} />
                  <Input type="email" value={registerForm.email} onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                    className="pl-11 h-12 text-base rounded-xl" placeholder={t('auth.emailPlaceholder')} required data-testid="mobile-register-email" />
                </div>
              </div>
              <div className="space-y-2">
                <Label style={{ color: "#3e4850" }}>{t('common.password')}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: "#bdc8d1" }} />
                  <Input type={showPassword ? "text" : "password"} value={registerForm.password} onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                    className="pl-11 pr-11 h-12 text-base rounded-xl" placeholder={t('auth.createPassword')} required data-testid="mobile-register-password" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: "#bdc8d1" }}>
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              <label className="flex items-start gap-3 cursor-pointer p-3 rounded-xl" style={{ background: "#f7f9fe", border: "1px solid rgba(189,200,209,0.3)" }}>
                <input
                  type="checkbox"
                  checked={registerForm.age_confirmed}
                  onChange={(e) => setRegisterForm({ ...registerForm, age_confirmed: e.target.checked })}
                  className="mt-0.5 w-4 h-4"
                  style={{ accentColor: "#00658D" }}
                  data-testid="mobile-register-age-checkbox"
                  required
                />
                <span className="text-xs" style={{ color: "#3e4850" }}>
                  {t('auth.ageConfirmLabel') || 'Kinnitan, et olen vähemalt 18-aastane ning nõustun Privaatsuspoliitika ja Meditsiinilise vastutuse välistamisega.'}
                </span>
              </label>
              <button type="submit" disabled={loading || !registerForm.age_confirmed} className="w-full h-12 text-base rounded-full text-white font-semibold disabled:opacity-50"
                style={{ background: btnGradient }} data-testid="mobile-register-submit">
                {loading ? "..." : t('auth.registerButton')}
              </button>
            </form>
          )}

          <div className="flex items-center gap-4 my-6">
            <div className="flex-1 h-px" style={{ background: "#dfe3e8" }} />
            <span className="text-sm" style={{ color: "#6e7881" }}>{t('auth.orContinueWith')}</span>
            <div className="flex-1 h-px" style={{ background: "#dfe3e8" }} />
          </div>

          <Button type="button" variant="outline" onClick={handleGoogleLogin} className="w-full h-12 text-base rounded-xl gap-3" data-testid="mobile-google-login">
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {t('auth.googleButtonText')}
          </Button>
        </div>
      </div>
    );
  }

  // Main Mobile Landing
  return (
    <div className="min-h-screen flex flex-col" style={{ fontFamily: "'Inter', sans-serif", background: "#f7f9fe", color: "#181c20" }}>
      {/* Mobile Header */}
      <nav className="sticky top-0 z-50 backdrop-blur-xl" style={{ background: "rgba(248,250,254,0.8)", boxShadow: clinicalShadow }}>
        <div className="flex justify-between items-center w-full px-6 py-4 max-w-lg mx-auto">
          <Link to="/" data-testid="mobile-header-brand" className="flex items-center gap-2 transition-opacity hover:opacity-80">
            <span className="w-7 h-7 rounded-lg overflow-hidden bg-white border border-slate-200 flex items-center justify-center">
              <img src="/brand/logo.webp" alt="Kilpnäärme Kliinik" className="w-6 h-6 object-contain" />
            </span>
            <span className="text-base font-bold tracking-tight" style={{ color: "#00658D" }}>
              {t('landing.title')}
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <Link
              to="/blog"
              data-testid="mobile-header-blog-link"
              className="text-sm font-medium transition-all"
              style={{ color: "#00658D" }}
            >
              {t("blog.nav")}
            </Link>
            <LanguageSwitcher />
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="flex-1 max-w-lg mx-auto w-full px-6 pt-12 pb-24 flex flex-col items-center">
        {/* Hero */}
        <div className="text-center mb-16">
          <h1 className="text-4xl font-black tracking-tight leading-[1.1] mb-4" style={{ color: "#181c20" }}>
            {t('landing.heroTitle') || "Kas oled tüdinud arsti vastuvõtu ootamisest?"}
          </h1>
          <p className="text-2xl font-extrabold tracking-tight leading-tight mb-6" style={{ color: "#00658D" }}>
            {t('landing.heroHighlight') || "Pikkadest järjekordadest?"}
          </p>
          <p className="max-w-[320px] mx-auto text-base font-semibold" style={{ color: "#3e4850" }}>
            {t('landing.description')}
          </p>
        </div>

        {/* Steps (matches desktop "How It Works" — 5 steps) */}
        <h2 className="sr-only">{t('landing.howItWorks')}</h2>
        <div className="grid grid-cols-1 gap-4 w-full mb-12" data-testid="mobile-steps">
          {[
            { step: "01", titleKey: "landing.step1Title", descKey: "landing.step1Desc", Icon: ClipboardList },
            { step: "02", titleKey: "landing.step2Title", descKey: "landing.step2Desc", Icon: FlaskConical },
            { step: "03", titleKey: "landing.step3Title", descKey: "landing.step3Desc", Icon: ScanLine },
            { step: "04", titleKey: "landing.step4Title", descKey: "landing.step4Desc", Icon: CreditCard },
            { step: "05", titleKey: "landing.step5Title", descKey: "landing.step5Desc", Icon: CheckCircle },
          ].map(({ step, titleKey, descKey, Icon }) => (
            <div
              key={step}
              className="rounded-xl p-5 flex items-start gap-4"
              style={{ background: "#fff", boxShadow: clinicalShadow }}
              data-testid={`mobile-step-${step}`}
            >
              <div className="p-3 rounded-xl flex-shrink-0" style={{ background: "rgba(198,231,255,0.4)" }}>
                <Icon className="w-6 h-6" style={{ color: "#00658D" }} />
              </div>
              <div>
                <span className="text-[11px] font-bold uppercase tracking-widest" style={{ color: "#00658D" }}>
                  {t('landing.step') || "Samm"} {step}
                </span>
                <h3 className="font-bold text-base mt-0.5 mb-1 leading-tight" style={{ color: "#181c20" }}>
                  {t(titleKey)}
                </h3>
                {t(descKey) && (
                  <p className="text-sm leading-relaxed" style={{ color: "#3e4850" }}>
                    {t(descKey)}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* CTA — Benjamin Franklin */}
        <div className="w-full mt-auto rounded-3xl p-8 text-center mb-6" style={{ background: "linear-gradient(180deg, #00658d 0%, #003547 100%)", color: "#fff" }} data-testid="mobile-cta-quote">
          <h2 className="text-3xl font-extrabold leading-tight whitespace-pre-line mb-3">
            {t('landing.ctaTitle')}
          </h2>
          <p className="text-base italic" style={{ color: "rgba(255,255,255,0.85)" }}>
            — {t('landing.ctaAttribution')}
          </p>
        </div>

        {/* CTA Button */}
        <div className="w-full">
          <button
            onClick={() => setShowAuth(true)}
            className="w-full text-white font-semibold py-5 px-8 rounded-full text-lg flex justify-center items-center gap-3 transition-all duration-300 active:scale-95"
            style={{ background: btnGradient, boxShadow: "0 12px 24px rgba(0,101,141,0.3)" }}
            data-testid="mobile-get-started-btn"
          >
            <span>{t('landing.forPatients') || "Patsientidele"}</span>
            <ArrowRight className="w-5 h-5" />
          </button>
          <p className="mt-3 text-center text-xs inline-flex items-center gap-1.5 mx-auto" style={{ color: "#6e7881" }} data-testid="mobile-estonia-only">
            <MapPin className="w-3.5 h-3.5" style={{ color: "#00658D" }} />
            {t('common.estoniaOnlyNote') || 'Teenus on saadaval ainult Eestis.'}
          </p>
        </div>
      </main>

      {/* Footer links */}
      <div className="px-6 pb-6 pt-2 max-w-lg mx-auto w-full">
        <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 text-xs" style={{ color: "#64748b" }}>
          <Link to="/privacy" className="hover:underline" data-testid="mobile-footer-privacy">
            {t("common.privacyPolicy")}
          </Link>
          <Link to="/disclaimer" className="hover:underline" data-testid="mobile-footer-disclaimer">
            {t("common.medicalDisclaimer")}
          </Link>
          <a href="mailto:support@kilpnäärmekliinik.ee" className="hover:underline">
            {t("blog.contact")}
          </a>
        </div>
      </div>

      {/* Bottom tonal shift */}
      <div className="h-20 w-full" style={{ background: "#f1f4f9" }} />
    </div>
  );
}
